import { useMemo, useState } from "react"
import styled from "styled-components"

import {
  Select,
  spacing,
  Text,
  TextArea,
  TextField,
  TSelectOptions,
} from "@minuthq/meatball-ui-react"
import { FormikProps } from "formik"

import { HomesDropdown } from "src/components/Dropdown/HomesDropdown"
import { NativeDateTimeField } from "src/components/Paradise/NativeDateTimeField"
import { TMaybeHome } from "src/data/homes/types/homeTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchMembers } from "src/data/organizations/queries/memberQueries"
import { TTaskPostBody } from "src/data/taskManagement/taskManagementTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { ComboboxStateful } from "src/ui/Combobox/ComboboxStateful"

export function CreateTaskForm({ form }: { form: FormikProps<TTaskPostBody> }) {
  const { tPlaceholder } = useTranslate()
  const { orgId } = useOrganization()

  const [selectedHome, setSelectedHome] = useState<TMaybeHome>(null)

  // This is paginated but a name search filter will be added to the backend so we can search for members
  const fetchMembers = useFetchMembers({
    orgId,
  })

  const memberOptions = useMemo(() => {
    const members = fetchMembers.data?.members ?? []

    return members.map((mem) => ({
      label: mem.name,
      value: mem.member_id,
    }))
  }, [fetchMembers.data?.members])

  // This will eventually come from the backend when GXP-1117 is implemented
  const taskCategory: TSelectOptions<TTaskPostBody["category"]> = [
    { label: tPlaceholder("Cleaning"), value: "cleaning" },
    { label: tPlaceholder("Maintenance"), value: "maintenance" },
    { label: tPlaceholder("Other"), value: "other" },
  ]

  return (
    <FormBox>
      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Title")}
        </Text>
        <TextField
          value={form.values.title}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              title: value,
            }))
          }}
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Unit")}
        </Text>

        <HomesDropdown
          value={selectedHome}
          onSelect={(home) => {
            setSelectedHome(home)
            form.setValues((prev) => ({
              ...prev,
              home_id: home?.home_id,
            }))
          }}
          required={true}
          initialHomeId={form.values.home_id}
          hideLabel={true}
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Task type")}
        </Text>
        <Select
          value={form.values.category}
          options={taskCategory}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              category: value as TTaskPostBody["category"],
            }))
          }}
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Description")}
        </Text>
        <TextArea
          value={form.values.description}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              description: value,
            }))
          }}
          minRows={3}
          maxRows={5}
        />
      </div>

      <FormRow>
        <div>
          <Text variant="subtitle" marginBottom={spacing.XS2}>
            {tPlaceholder("Due date")}
          </Text>

          <NativeDateTimeField
            value={
              form.values.time_window.due_date
                ? new Date(form.values.time_window.due_date)
                : null
            }
            onChange={(value) => {
              form.setValues((prev) => ({
                ...prev,
                time_window: {
                  ...prev.time_window,
                  due_date: value.toISOString(),
                },
              }))
            }}
            required
          />
        </div>

        <div>
          <Text variant="subtitle" marginBottom={spacing.XS2}>
            {tPlaceholder("Assign to")}
          </Text>

          <ComboboxStateful
            options={memberOptions}
            selectedValue={form.values.assigned_to.assignee_id ?? ""}
            onChange={(value) => {
              form.setValues((prev) => ({
                ...prev,
                assigned_to: {
                  ...prev.assigned_to,
                  assignee_id: value,
                },
              }))
            }}
            onSearch={() => {}}
          />
        </div>
      </FormRow>
    </FormBox>
  )
}

const FormBox = styled.div`
  display: grid;
  gap: ${spacing.L};
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.M};
`
